import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { groupBy } from 'lodash';
import moment from 'moment-timezone';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

import { actions } from '../redux/actions';
import Strings from '../../../../Strings';
import { TextInput } from '../../../../components/PageHeader/TextInput';
import Select from '../../../../components/Select';
import AdvancedFilters, { Filter } from '../../../../components/AdvancedFilters/AdvancedFilters';
import { getRelevantFilters } from '../../../../components/AdvancedFilters/helpers';
import { getFiltersDefinition } from '../../../../components/AdvancedFilters/FiltersDefinition';
import PatientLogItem from './PatientLogItem';
import { DATE_FORMAT_YEAR_MONTH_DAY, DATE_TIME_SPACE_12_UPPERCASE, RPM_PATIENT_STATUS } from '../../../../constants';
import AdvancedFiltersBar from '../../../../components/AdvancedFilters/AdvancedFiltersBar';
import { DateFilters } from '../../../../utils/DateFilters';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../../components/PageHeader/Button';
import { openModalAction } from '../../../../actions/modal';
import { isAccessProgram } from '../../../../utils/cmsPrograms';
import { DOCTOR, SUPERUSER } from '../../../../utils/userRoles';
import DropdownMenu from '../../../../components/DropdownMenu/DropdownMenu';
import { careUpdateActions, CREATE_CARE_ESCALATION_RESULT } from '../../../../actions/careUpdates';
import { notificationActions } from '../../../../components/Notification/redux/actions';

function PatientLog(props) {
  const {
    patientId,
    patientDetails,
    notes,
    canManageCareUpdates,
    openCareUpdates,
    createEscalation: createEscalationAction,
    showNotification,
    getNotes,
    filters,
    refreshTimestamp,
  } = props;

  const [searchString, setSearchString] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState(DateFilters.AllTime);
  const [isCreatingEscalation, setIsCreatingEscalation] = useState(false);
  const creatingEscalation = React.useRef(false);
  const activeAccessEnrollments = Object.entries(patientDetails?.patientEnrollment?.enrollments || {})
    .filter(([, enrollment]) => isAccessProgram(enrollment.program)
      && enrollment.status === RPM_PATIENT_STATUS.activated && enrollment.accessAlignment?.confirmedAt);

  const createEscalation = async enrollmentId => {
    if (creatingEscalation.current) return;
    creatingEscalation.current = true;
    setIsCreatingEscalation(true);
    try {
      const response = await createEscalationAction(enrollmentId);
      if (response?.type !== CREATE_CARE_ESCALATION_RESULT) {
        showNotification(response?.response?.data?.error?.message || Strings.accessCareUpdates.requestFailed, 5000, true);
        return;
      }
      getNotes();
      showNotification(Strings.accessCareUpdates.escalationCreated);
      openCareUpdates({ patientId, patientDetails, enrollmentId, updateId: response.response.id });
    } catch {
      showNotification(Strings.accessCareUpdates.requestFailed, 5000, true);
    } finally {
      creatingEscalation.current = false;
      setIsCreatingEscalation(false);
    }
  };

  useEffect(() => {
    getNotes();
  }, [patientId, refreshTimestamp]);

  const onTextInputChange = e => {
    setSearchString(e.target.value);
  };

  const filterDataByAdvancedFilters = data => {
    const appliedFilters = getRelevantFilters(filters);
    let ret = data;
    if (!(appliedFilters.some(f => f.value === 'Note') && appliedFilters.some(f => f.value === 'Action'))) {
      if (appliedFilters.some(f => f.value === 'Note')) ret = ret.filter(n => n.action_completed === undefined);
      if (appliedFilters.some(f => f.value === 'Action')) ret = ret.filter(n => n.action_completed !== undefined);
    }
    if (!(appliedFilters.some(f => f.value === 'Completed') && appliedFilters.some(f => f.value === 'Open'))) {
      if (appliedFilters.some(f => f.value === 'Completed')) ret = ret.filter(n => n.action_completed === true);
      if (appliedFilters.some(f => f.value === 'Open')) ret = ret.filter(n => n.action_completed === false);
    }
    return ret;
  };

  const onDateRangeChanged = option => {
    if (option) setSelectedDateRange(option);
  };

  const getFilteredData = type => {
    let ret = [];
    if (!notes) return ret;
    ret = type === 'actions' ? notes.filter(n => n.action_completed !== undefined) : notes.filter(n => n.action_completed === undefined);
    
    ret = ret.filter(n => n.content.toLowerCase().includes(searchString.toLowerCase()));
    ret = ret.filter(
      n =>
        n.modification_time >= moment(selectedDateRange.dates.startDate()).unix() &&
        n.modification_time <= moment(selectedDateRange.dates.endDate()).unix(),
    );
    ret = filterDataByAdvancedFilters(ret);

    return ret;
  };

  const dataActions = getFilteredData('actions');
  const daysActions = groupBy(dataActions, x => moment(x.modification_time * 1000).format(DATE_FORMAT_YEAR_MONTH_DAY));

  const dataNotes = getFilteredData('notes');
  const daysNotes = groupBy(dataNotes, x => moment(x.modification_time * 1000).format(DATE_FORMAT_YEAR_MONTH_DAY));

  const content = (data, days) => {
    if (!days) return null;
    return (
      <OverlayScrollbarsComponent
        defer
        className="patient-log"
        options={{ scrollbars: { autoHide: 'leave', autoHideDelay: '100' } }}
      >
        {Object.keys(days).length > 0 ? (
          Object.keys(days)
            .sort()
            .reverse()
            .map(day => (
              <div className="patient-log" key={`patient-log-${day}`}>
                <div className="horizontal-rule" />
                <div className="patient-log-date">{moment(day).format('dddd, MMMM Do')}</div>
                {days[day]
                  .sort((a, b) => a.modification_time - b.modification_time)
                  .reverse()
                  .map(d => (
                    <PatientLogItem logItem={d} patientId={patientId} patientDetails={patientDetails} key={`patient-note-${d.id}`} />
                  ))}
              </div>
            ))
        ) : (
          <div className="empty-row-container">
            <div className="empty-row-inner">{Strings.noDataAvailable}</div>
          </div>
        )}
      </OverlayScrollbarsComponent>
    );
  };

  return (
    <div className="patient-log-widget">
      <div className="section-header">
        <div>{Strings.patientLog}</div>
        <div className="patient-log-filters">
          <TextInput class="search" placeholder={Strings.searchLog} onChange={onTextInputChange} />
          <Select
            data={[DateFilters.AllTime, DateFilters.ThisMonth, DateFilters.Last30Days, DateFilters.LastMonth]}
            onChange={onDateRangeChanged}
            value={selectedDateRange.value}
            menuPositionFixed={false}
          />
          <AdvancedFilters>
            <Filter definition={getFiltersDefinition().LogItemType} />
            <Filter definition={getFiltersDefinition().ActionStatus} />
          </AdvancedFilters>
        </div>
      </div>
      <AdvancedFiltersBar />
      <div className="patient-log-two-columns">
        <div className="section-header">
          <div>{Strings.actions}</div>
          {(canManageCareUpdates && activeAccessEnrollments.length === 1) && (
            <Button
              class={HEADER_BUTTON_DARK_BLUE}
              title={Strings.accessCareUpdates.createEscalation}
              disabled={isCreatingEscalation}
              onClick={() => createEscalation(activeAccessEnrollments[0][0])}
            />
          )}
          {(canManageCareUpdates && activeAccessEnrollments.length > 1) && (
            <DropdownMenu
              title={Strings.accessCareUpdates.createEscalation}
              options={activeAccessEnrollments.map(([id, enrollment]) => ({
                label: `${enrollment.program} — ${id}`,
                disabled: isCreatingEscalation,
                onSelect: () => createEscalation(id),
              }))}
            />
          )}
        </div>
        <div className="section-header">{Strings.capPatient.notes}</div>
        {content(dataActions, daysActions)}
        {content(dataNotes, daysNotes)}
      </div>
    </div>
  );
}

PatientLog.propTypes = {
  notes: PropTypes.array,
  patientId: PropTypes.string,
  patientDetails: PropTypes.object,
  canManageCareUpdates: PropTypes.bool,
  openCareUpdates: PropTypes.func,
  createEscalation: PropTypes.func,
  showNotification: PropTypes.func,
  getNotes: PropTypes.func,
  filters: PropTypes.array,
  refreshTimestamp: PropTypes.any,
};

const mapStateToProps = state => {
  const { patients } = state.superUser;
  return {
    notes: patients?.notes,
    canManageCareUpdates: [DOCTOR, SUPERUSER].includes(state.auth.role),
    filters: state.entities.advancedFilters.filters.items,
  };
};

const mapDispatchToProps = (dispatch, props) => ({
  getNotes: () => dispatch(actions.getNotes(props.patientId)),
  openCareUpdates: data => dispatch(openModalAction('access-care-updates', data)),
  createEscalation: id => dispatch(careUpdateActions.createEscalation(id)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PatientLog);
